/**
 * Workspaces API client
 * Handles workspace operations (create, retrieve, evidence, synthesis, chat)
 */

import { apiClient } from './client'
import type { CitationPaper } from './citations'

export interface Workspace {
  workspace_id: string
  user_id: string
  title?: string
  query: string
  field?: string
  filters?: {
    year_from?: number
    year_to?: number
    custom_instructions?: string
  }
  status: string
  created_at?: string
  updated_at?: string
}

export interface WorkspaceCreateRequest {
  query: string
  field?: string
  filters?: {
    year_from?: number
    year_to?: number
    custom_instructions?: string
  }
  title?: string
}

export interface PaperResult {
  paper_ref: string
  title: string
  authors: string[]
  year?: number
  doi?: string
  arxiv_id?: string
  abstract?: string
  venue?: string
  url?: string
  relevance_score?: number
  relevance_explanation?: string
  rank?: number
  citationCount?: number
  referenceCount?: number
  fieldsOfStudy?: string[]
  isOpenAccess?: boolean
}

export interface WorkspaceRetrieveRequest {
  max_results?: number
}

export interface EvidenceAddRequest {
  paper_ref: string
  workspace_tags?: string[]
  evidence_notes?: string
}

export interface WorkspaceMessage {
  message_id: string
  workspace_id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  citations?: string[]
  created_at?: string
}

export interface MessageRequest {
  role: 'user' | 'assistant'
  content: string
  citations?: string[]
}

export interface SynthesisRequest {
  output: string
  prompt_config?: Record<string, any>
  citations?: string[]
  version?: number
}

export interface WorkspaceSynthesis {
  synthesis_id: string
  workspace_id: string
  version: number
  output: string
  prompt_config?: Record<string, any>
  citations?: string[]
  created_at?: string
}

class WorkspacesAPI {
  /**
   * Create a new workspace
   */
  async createWorkspace(request: WorkspaceCreateRequest): Promise<Workspace> {
    return await apiClient.post('/api/workspaces', request)
  }

  /**
   * List all workspaces for the current user
   */
  async listWorkspaces(): Promise<Workspace[]> {
    return await apiClient.get('/api/workspaces')
  }

  /**
   * Get a specific workspace
   */
  async getWorkspace(workspaceId: string): Promise<Workspace> {
    return await apiClient.get(`/api/workspaces/${workspaceId}`)
  }

  /**
   * Update workspace (title, status, query, field, or filters)
   */
  async updateWorkspace(
    workspaceId: string,
    title?: string,
    status?: string,
    query?: string,
    field?: string,
    filters?: {
      year_from?: number
      year_to?: number
      custom_instructions?: string
    }
  ): Promise<Workspace> {
    const body: Record<string, any> = {}
    if (title !== undefined) body.title = title
    if (status !== undefined) body.status = status
    if (query !== undefined) body.query = query
    if (field !== undefined) body.field = field
    if (filters !== undefined) body.filters = filters
    
    // Only send request if there's something to update
    if (Object.keys(body).length === 0) {
      // Return current workspace if nothing to update
      return await this.getWorkspace(workspaceId)
    }
    
    return await apiClient.patch(`/api/workspaces/${workspaceId}`, body)
  }

  /**
   * Delete a workspace
   */
  async deleteWorkspace(workspaceId: string): Promise<{ success: boolean; message: string }> {
    return await apiClient.delete(`/api/workspaces/${workspaceId}`)
  }

  /**
   * Trigger paper retrieval for a workspace
   */
  async retrievePapers(
    workspaceId: string,
    request: WorkspaceRetrieveRequest = {}
  ): Promise<PaperResult[]> {
    return await apiClient.post(`/api/workspaces/${workspaceId}/retrieve`, request)
  }

  /**
   * Get retrieval results for a workspace
   */
  async getWorkspaceResults(workspaceId: string): Promise<PaperResult[]> {
    return await apiClient.get(`/api/workspaces/${workspaceId}/results`)
  }

  /**
   * Add a paper to workspace evidence
   */
  async addEvidence(
    workspaceId: string,
    request: EvidenceAddRequest
  ): Promise<{ success: boolean }> {
    return await apiClient.post(`/api/workspaces/${workspaceId}/evidence`, request)
  }

  /**
   * Remove a paper from workspace evidence
   */
  async removeEvidence(
    workspaceId: string,
    paperRef: string
  ): Promise<{ success: boolean }> {
    return await apiClient.delete(`/api/workspaces/${workspaceId}/evidence/${paperRef}`)
  }

  /**
   * Get all evidence papers for a workspace
   */
  async getWorkspaceEvidence(workspaceId: string): Promise<any[]> {
    return await apiClient.get(`/api/workspaces/${workspaceId}/evidence`)
  }

  /**
   * Add a message to workspace chat
   */
  async addMessage(
    workspaceId: string,
    request: MessageRequest
  ): Promise<WorkspaceMessage> {
    return await apiClient.post(`/api/workspaces/${workspaceId}/messages`, request)
  }

  /**
   * Get all messages for a workspace
   */
  async getMessages(workspaceId: string): Promise<WorkspaceMessage[]> {
    return await apiClient.get(`/api/workspaces/${workspaceId}/messages`)
  }

  /**
   * Save a synthesis output
   */
  async saveSynthesis(
    workspaceId: string,
    request: SynthesisRequest
  ): Promise<WorkspaceSynthesis> {
    return await apiClient.post(`/api/workspaces/${workspaceId}/syntheses`, request)
  }

  /**
   * Get all syntheses for a workspace
   */
  async getSyntheses(workspaceId: string): Promise<WorkspaceSynthesis[]> {
    return await apiClient.get(`/api/workspaces/${workspaceId}/syntheses`)
  }

  /**
   * Generate a synthesis from workspace evidence
   */
  async generateSynthesis(
    workspaceId: string,
    mode: string = 'answer_question',
    evidenceOnly: boolean = true,
    provider?: string,
    model?: string
  ): Promise<WorkspaceSynthesis> {
    return await apiClient.post(`/api/workspaces/${workspaceId}/syntheses/generate`, {
      mode,
      evidence_only: evidenceOnly,
      ...(provider && { provider }),
      ...(model && { model })
    })
  }

  /**
   * Generate a chat response
   */
  async generateChatResponse(
    workspaceId: string,
    message: string,
    evidenceOnly: boolean = true,
    provider?: string,
    model?: string
  ): Promise<WorkspaceMessage> {
    return await apiClient.post(`/api/workspaces/${workspaceId}/messages/generate`, {
      message,
      evidence_only: evidenceOnly,
      ...(provider && { provider }),
      ...(model && { model })
    })
  }
}

export const workspacesAPI = new WorkspacesAPI()
export default workspacesAPI

